import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks";
import {
  type GetPolicyResponse,
  WaiversPoliciesApi,
} from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface UsePoliciesGetByIdProps {
  ids: number[];
}

export const usePoliciesGetById = ({ ids }: UsePoliciesGetByIdProps) => {
  const accessToken = useAccessToken();

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: [QueryKeys.Waivers, "byId", id],
      queryFn: () => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }
        const service = createAuthenticatedApi(WaiversPoliciesApi, accessToken);
        return service.getPolicyById({
          id,
        });
      },
      enabled: !!accessToken && !!id && ids.length > 0,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    })),
  });

  const policies = useMemo(() => {
    return queries
      .map((query) => query.data)
      .filter((policy): policy is GetPolicyResponse => policy !== undefined);
  }, [queries]);

  const isLoading = queries.some((query) => query.isLoading);

  return {
    policies,
    isLoading,
  };
};
